//#region src/pd-badge-order/flip-animator.ts
/**
* FLIP animation utility for smooth list reordering.
*
* FLIP = First, Last, Invert, Play
* 1. First:  Capture positions before DOM change (capturePositions)
* 2. Last:   DOM change happens (Lit re-render)
* 3. Invert: Calculate deltas, apply inverse transform
* 4. Play:   Animate transform back to none
*
* Generic over the id attribute: defaults to `data-badge-id`
* (pd-badge-order); other consumers (e.g. pd-gallery-input with
* `data-item-id`) pass their own attribute name.
*/
/**
* Capture current positions of all matching elements in a container.
* Call this BEFORE the DOM change.
*
* @param idAttribute - Attribute holding the stable item id. Default: `data-badge-id`.
* @returns Map of item-id → DOMRect
*/
function capturePositions(container, selector, idAttribute = "data-badge-id") {
	const positions = /* @__PURE__ */ new Map();
	container.querySelectorAll(selector).forEach((el) => {
		const id = el.getAttribute(idAttribute);
		if (id) positions.set(id, el.getBoundingClientRect());
	});
	return positions;
}
/**
* Animate elements from their old positions to their new positions using FLIP.
* Call this AFTER the DOM change (e.g., in updated() or requestAnimationFrame).
* Respects `prefers-reduced-motion`.
*
* @param container - The container element holding the items
* @param selector - CSS selector for item elements
* @param oldPositions - Positions captured before the DOM change
* @param options - Animation options (incl. `idAttribute`, default `data-badge-id`)
*/
function animateFlip(container, selector, oldPositions, options = {}) {
	const { duration = 200, easing = "ease-out", idAttribute = "data-badge-id" } = options;
	if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
	container.querySelectorAll(selector).forEach((el) => {
		const htmlEl = el;
		const id = htmlEl.getAttribute(idAttribute);
		if (!id) return;
		const oldPos = oldPositions.get(id);
		if (!oldPos) return;
		const newPos = htmlEl.getBoundingClientRect();
		const deltaX = oldPos.left - newPos.left;
		const deltaY = oldPos.top - newPos.top;
		if (Math.abs(deltaX) < 1 && Math.abs(deltaY) < 1) return;
		htmlEl.animate([{ transform: `translate(${deltaX}px, ${deltaY}px)` }, { transform: "translate(0, 0)" }], {
			duration,
			easing,
			fill: "none"
		});
	});
}
//#endregion
export { animateFlip, capturePositions };
